/**
 * The shop payment step's behavior half (studio-shop D6): the card payment's
 * controller the website's booking checkout uses (usePaymentFlow), with the
 * shop order's intent request. The server owns the one chargeable payment, so a
 * reload or a second tab resumes it. Once Stripe takes the payment the bag is
 * emptied and the order page opens, which confirms it; an order paid elsewhere
 * opens it too. An order the sweep abandoned hands back to the checkout, which
 * starts a new one at today's prices. shop-self-fulfilment D5: so does an order
 * holding stock once its card step's time (the intent's `expiresAt`) passes,
 * before the sweep cancels its payment; a payment already sent is waited for,
 * and only a payment that did not go through hands back. Draws nothing.
 */
import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CardCheckoutPanelHandle } from '@/components/payment/CardCheckoutPanel';
import type { CheckoutError } from '@/hooks/checkoutError';
import { usePaymentFlow, type PaymentFlowTransport, type PaymentStatusRead } from '@/hooks/payment/usePaymentFlow';
import type { PaymentIntentRequest } from '@/hooks/useCreatePaymentIntent';
import type { CheckoutEntry } from '@/types/checkout';

import { forgetCheckout, shopMoney, shopOrderIntentRequest } from '../../api/shop';
import { useSiteStudio } from '../../api/site';
import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useBag } from '../../hooks/useBag';
import { contract, type ShopPaymentStepActions, type ShopPaymentStepEvents, type ShopPaymentStepState } from './contract';

export interface ShopPaymentStepInput extends PieceInput<ShopPaymentStepEvents> {
  /** The unpaid order this checkout made. */
  readonly orderId: string;
  /** This browser's claim on it (D8); null for its signed-in owner without one. */
  readonly claimToken: string | null;
  /** The order's total, a money string, and its currency. */
  readonly total: string;
  readonly currency: string;
  /** The order was abandoned (24 hours unpaid), or its set-aside time passed: the checkout starts a new one. */
  readonly onExpired: () => void;
}

export type ShopPaymentStepRuntime = PieceRuntime<typeof contract, ShopPaymentStepState, ShopPaymentStepActions> & {
  /** The card panel's props. */
  readonly panel: {
    readonly key: number;
    readonly ref: RefObject<CardCheckoutPanelHandle>;
    readonly entry: CheckoutEntry;
    readonly intentRequest: PaymentIntentRequest;
    readonly onCompleteChange: (complete: boolean) => void;
    readonly onIntentReady: (expiresAt: string | null) => void;
    readonly onSubmitInFlightChange: (inFlight: boolean) => void;
    readonly onPaymentSubmitted: (p: { stripePaymentIntentId: string }) => void;
    readonly onDecline: (d: { title: string } | null) => void;
    readonly onError: (error: CheckoutError) => void;
    readonly onConflict: () => void;
  };
};

/** The order page confirms the payment, so the step itself reads no status. */
const noStatusRead = (): PaymentStatusRead => ({});
const noFlow = () => {};

export function useShopPaymentStep(input: ShopPaymentStepInput): ShopPaymentStepRuntime {
  const options = usePieceOptions(contract, input.options);
  const navigate = useNavigate();
  const { clear } = useBag();
  const seller = useSiteStudio().data?.name ?? null;
  const { orderId, claimToken, onExpired } = input;

  const entry = useMemo<CheckoutEntry>(() => ({ kind: 'shopOrder', orderId }), [orderId]);
  const transport = useMemo<PaymentFlowTransport>(
    () => ({ intentRequest: shopOrderIntentRequest(orderId, claimToken), useStatus: noStatusRead, refetchFlow: noFlow }),
    [orderId, claimToken],
  );
  const openOrder = useCallback(
    () => navigate(`/shop/orders/${encodeURIComponent(orderId)}`, { replace: true, state: { thanks: true } }),
    [navigate, orderId],
  );
  const flow = usePaymentFlow({ entry, transport, onBooked: openOrder });

  // Paid or handed back: nothing more for this step to do.
  const done = useRef(false);
  const { timerExpired, submitting } = flow;
  useEffect(() => {
    if (!timerExpired || submitting || done.current) return;
    done.current = true;
    onExpired();
  }, [timerExpired, submitting, onExpired]);

  const base = flow.panel;
  const panel = useMemo(
    () => ({
      ...base,
      onPaymentSubmitted: () => {
        done.current = true;
        openOrder();
        clear();
        // shop-self-fulfilment D5: paid, so the next checkout in this tab is a new one, never this order again.
        forgetCheckout();
      },
      // Another tab's payment for this order went through.
      onConflict: openOrder,
      onError: (error: CheckoutError) => {
        if (error.code === 'ORDER_ABANDONED') onExpired();
        else base.onError(error);
      },
    }),
    [base, clear, openOrder, onExpired],
  );

  const { pay } = flow;
  const actions = useMemo<ShopPaymentStepActions>(() => ({ pay }), [pay]);

  return {
    state: {
      amount: shopMoney(input.total, input.currency),
      complete: flow.complete,
      paying: flow.submitting,
      decline: flow.decline?.title ?? null,
      // The website offers no other method beside the card (row P6), and a shop order holds no time.
      declineNote: flow.decline ? 'No charge was made. Try a different card.' : null,
      notice: flow.cardRefused ? 'Card payment is paused right now. Please try again later.' : null,
      seller,
    },
    actions,
    options,
    panel,
  };
}
